// ABOUTME: Challenge definitions for gamified learning experiences
// ABOUTME: Provides structured tasks with requirements and rewards

import type { Challenge } from '@/types/content'

/**
 * Challenge definitions for Levels 1-3 (MVP Phase)
 * Each level has 4 challenges
 */
export const CHALLENGES: Challenge[] = [
  // ==========================================
  // LEVEL 1: Inventory -- "Know your wardrobe"
  // ==========================================
  {
    id: 'photograph-and-tag',
    title: 'Wardrobe Census',
    description:
      'Photograph and tag 10 items from your wardrobe. The first step to dressing well is knowing what you own.',
    shortDescription: 'Scan and tag 10 items',
    category: 'wardrobe_basics',
    difficulty: 'beginner',
    xpReward: 100,
    levelRequired: 1,
    type: 'wardrobe_audit',
    requirements: [
      {
        id: 'scan-10-items',
        description: 'Photograph and tag 10 wardrobe items',
        type: 'scan_items',
        target: 10,
      },
    ],
    hints: [
      'Start with items you reach for most often',
      'Cover different categories: tops, bottoms, shoes',
      'Use natural lighting for the best photos',
    ],
    icon: 'Camera',
    color: '#3b82f6',
    relatedModules: ['wardrobe-basics-101', 'closet-organization'],
    order: 1,
  },

  {
    id: 'identify-most-worn',
    title: 'Favourites Revealed',
    description:
      'Log outfits for 5 days to identify your most-worn items. Understanding your go-to pieces is the foundation of building on your style.',
    shortDescription: 'Find your 5 most-worn items',
    category: 'wardrobe_basics',
    difficulty: 'beginner',
    xpReward: 125,
    levelRequired: 1,
    type: 'wardrobe_audit',
    requirements: [
      {
        id: 'log-5-outfits',
        description: 'Log outfits for 5 days',
        type: 'log_outfit',
        target: 5,
      },
      {
        id: 'identify-top-5',
        description: 'Identify your 5 most-worn items',
        type: 'identify_most_worn',
        target: 5,
      },
    ],
    hints: [
      'Log every outfit, even casual ones',
      'After 5 days, check your wardrobe analysis',
      'Look at the usage stats to spot patterns',
    ],
    icon: 'Star',
    color: '#3b82f6',
    relatedModules: ['wardrobe-basics-101'],
    order: 2,
  },

  {
    id: 'find-forgotten-items',
    title: 'Hidden Treasures',
    description:
      'Find 3 items in your wardrobe you forgot you owned. Rediscovering neglected pieces opens up outfit possibilities.',
    shortDescription: 'Rediscover 3 forgotten items',
    category: 'wardrobe_basics',
    difficulty: 'beginner',
    xpReward: 100,
    levelRequired: 1,
    type: 'wardrobe_audit',
    requirements: [
      {
        id: 'find-3-forgotten',
        description: 'Interact with 3 items from the neglected list',
        type: 'find_forgotten_items',
        target: 3,
      },
    ],
    hints: [
      'Check the Insights page for neglected items',
      'Look at items not worn in 30+ days',
      'Try wearing one of them this week',
    ],
    icon: 'Search',
    color: '#3b82f6',
    relatedModules: ['closet-organization'],
    order: 3,
  },

  {
    id: 'outfit-from-neglected',
    title: 'Second Chances',
    description:
      'Build an outfit using at least one neglected item. Prove that forgotten pieces still have style potential.',
    shortDescription: 'Wear a neglected item',
    category: 'wardrobe_basics',
    difficulty: 'beginner',
    xpReward: 150,
    levelRequired: 1,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'use-neglected',
        description: 'Log an outfit containing a neglected item',
        type: 'use_neglected_item',
        target: 1,
      },
      {
        id: 'create-outfit-neglected',
        description: 'Create and save the outfit',
        type: 'create_outfit',
        target: 1,
      },
    ],
    hints: [
      'Check Insights for items not worn in 30+ days',
      'Pair the neglected item with reliable favourites',
      'Think about what made you stop wearing it',
    ],
    icon: 'RefreshCw',
    color: '#3b82f6',
    relatedModules: ['building-foundation-wardrobe'],
    order: 4,
  },

  // ==========================================
  // LEVEL 2: Combinations -- "Use your wardrobe better"
  // ==========================================
  {
    id: 'five-from-eight',
    title: 'Capsule Creator',
    description:
      'Build 5 different outfits using no more than 8 unique items. Discover how versatile your wardrobe really is.',
    shortDescription: 'Build 5 outfits from 8 items',
    category: 'style',
    difficulty: 'intermediate',
    xpReward: 200,
    levelRequired: 2,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'create-5-outfits',
        description: 'Create 5 outfits from a pool of 8 items',
        type: 'create_outfit',
        target: 5,
        metadata: { maxUniqueItems: 8 },
      },
    ],
    hints: [
      'Pick items that layer and mix well',
      'Neutral bottoms pair with multiple tops',
      'One good pair of shoes can anchor many outfits',
    ],
    icon: 'Layers',
    color: '#10b981',
    relatedModules: ['building-foundation-wardrobe'],
    order: 5,
  },

  {
    id: 'without-go-to',
    title: 'Comfort Zone Exit',
    description:
      'Create a work-appropriate outfit without using your most-worn top. Break the habit of reaching for the same shirt.',
    shortDescription: 'Outfit without your go-to shirt',
    category: 'style',
    difficulty: 'intermediate',
    xpReward: 175,
    levelRequired: 2,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'outfit-no-favourite',
        description: 'Create an outfit excluding your most-worn top',
        type: 'create_outfit',
        target: 1,
        metadata: { excludeMostWornTop: true },
      },
    ],
    hints: [
      'Check your usage stats to identify your go-to top',
      'Think about what makes that shirt your favourite and find similar qualities',
      'This is about expanding your rotation, not abandoning what works',
    ],
    icon: 'Shuffle',
    color: '#10b981',
    relatedModules: ['color-combinations'],
    order: 6,
  },

  {
    id: 'one-item-three-ways',
    title: 'Triple Threat',
    description:
      'Make one item work in 3 different outfits. Prove the versatility of a single piece by wearing it across different looks.',
    shortDescription: '1 item in 3 outfits',
    category: 'style',
    difficulty: 'intermediate',
    xpReward: 200,
    levelRequired: 2,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'versatile-item',
        description: 'Use one item in 3 different logged outfits',
        type: 'versatile_item',
        target: 1,
        metadata: { outfitCount: 3 },
      },
    ],
    hints: [
      'Chinos, navy blazers, and white shirts are naturally versatile',
      'Change the formality by swapping other pieces',
      'Log each outfit to track your progress',
    ],
    icon: 'Repeat',
    color: '#10b981',
    relatedModules: ['building-foundation-wardrobe'],
    order: 7,
  },

  {
    id: 'no-repeat-week',
    title: 'Seven Unique Days',
    description:
      'Go a full week without repeating an outfit. Log 7 consecutive days of unique looks.',
    shortDescription: 'No outfit repeats for 7 days',
    category: 'style',
    difficulty: 'intermediate',
    xpReward: 250,
    levelRequired: 2,
    timeLimit: 7,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'week-unique',
        description: 'Log 7 consecutive days with unique outfits',
        type: 'no_repeat_week',
        target: 1,
      },
    ],
    hints: [
      'Plan your outfits at the start of the week',
      'Swap key pieces rather than entire outfits',
      'Accessories can differentiate similar combinations',
    ],
    icon: 'Calendar',
    color: '#10b981',
    relatedModules: ['closet-organization'],
    order: 8,
  },

  // ==========================================
  // LEVEL 3: Occasions -- "Dress for context"
  // ==========================================
  {
    id: 'four-occasions',
    title: 'Occasion Ready',
    description:
      'Build outfits for 4 different occasions: work, weekend, date night, and active. Show range in your wardrobe.',
    shortDescription: 'Outfits for 4 occasions',
    category: 'style',
    difficulty: 'advanced',
    xpReward: 250,
    levelRequired: 3,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'occasion-outfits',
        description: 'Create outfits for work, weekend, date, and active occasions',
        type: 'occasion_outfit',
        target: 4,
        metadata: { occasions: ['work', 'weekend', 'date', 'active'] },
      },
    ],
    hints: [
      'Match formality to the setting',
      'Weekend and active outfits can overlap',
      'Date night should feel intentional but not overdressed',
    ],
    icon: 'Briefcase',
    color: '#8b5cf6',
    relatedModules: ['styling-fundamentals'],
    order: 9,
  },

  {
    id: 'cross-occasion-items',
    title: 'Wardrobe Chameleon',
    description:
      'Identify 3 items in your wardrobe that work across multiple occasions. The most valuable pieces are the most versatile.',
    shortDescription: 'Find 3 multi-occasion items',
    category: 'style',
    difficulty: 'advanced',
    xpReward: 225,
    levelRequired: 3,
    type: 'wardrobe_audit',
    requirements: [
      {
        id: 'multi-occasion-items',
        description: 'Identify 3 items worn in different occasions',
        type: 'multi_occasion_item',
        target: 3,
      },
    ],
    hints: [
      'Dark chinos work in casual and smart-casual settings',
      'A well-fitted blazer transitions from work to dinner',
      'Quality leather shoes dress up or down',
    ],
    icon: 'Sparkles',
    color: '#8b5cf6',
    relatedModules: ['building-foundation-wardrobe'],
    order: 10,
  },

  {
    id: 'find-wardrobe-gap',
    title: 'Gap Analysis',
    description:
      'Review your wardrobe analysis and identify the "nothing to wear" gap. Understanding what you lack is as important as knowing what you have.',
    shortDescription: 'Find your wardrobe gap',
    category: 'wardrobe',
    difficulty: 'advanced',
    xpReward: 200,
    levelRequired: 3,
    type: 'wardrobe_audit',
    requirements: [
      {
        id: 'view-gaps',
        description: 'Review the Insights page gap analysis',
        type: 'research',
        target: 1,
      },
    ],
    hints: [
      'Check the Insights page for gap analysis',
      'Look at category, formality, and season coverage',
      'Consider which gap affects you most often',
    ],
    icon: 'Search',
    color: '#8b5cf6',
    relatedModules: ['closet-organization', 'building-foundation-wardrobe'],
    order: 11,
  },

  {
    id: 'grab-and-go',
    title: 'Grab and Go',
    description:
      'Assemble a reliable grab-and-go outfit: something you can throw on for any low-stakes occasion and feel confident.',
    shortDescription: 'Build your reliable default outfit',
    category: 'style',
    difficulty: 'advanced',
    xpReward: 200,
    levelRequired: 3,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'grab-go-outfit',
        description: 'Create and save a grab-and-go outfit',
        type: 'create_outfit',
        target: 1,
        metadata: { tag: 'grab-and-go' },
      },
    ],
    hints: [
      'Pick items that fit well and require no thought',
      'Stick to reliable colours and comfortable fabrics',
      'This outfit should work for errands, coffee, or casual meetups',
    ],
    icon: 'Zap',
    color: '#8b5cf6',
    relatedModules: ['styling-fundamentals'],
    order: 12,
  },

  // ==========================================
  // LEVEL 4: Curation -- "Refine your wardrobe"
  // ==========================================
  {
    id: 'capsule-week',
    title: 'Capsule Week',
    description:
      'Live out of a 15-item capsule wardrobe for a full week. Prove that fewer pieces means more style.',
    shortDescription: '15 items, 7 days',
    category: 'wardrobe',
    difficulty: 'intermediate',
    xpReward: 250,
    levelRequired: 4,
    timeLimit: 7,
    type: 'wardrobe_audit',
    requirements: [
      {
        id: 'capsule-7-days',
        description: 'Log outfits for 7 days using no more than 15 items',
        type: 'log_outfit',
        target: 7,
        metadata: { maxUniqueItems: 15 },
      },
    ],
    hints: [
      'Plan all 7 outfits before the week starts',
      'Select versatile items that mix and match easily',
      'Accessories can change the entire look without adding to the count',
    ],
    icon: 'Package',
    color: '#06b6d4',
    relatedModules: ['capsule-wardrobe-principles', 'versatile-pieces'],
    order: 13,
  },

  {
    id: 'cost-per-wear-audit',
    title: 'Investment Review',
    description:
      'Calculate the cost-per-wear for your 5 most expensive items. Discover which investments paid off.',
    shortDescription: 'Audit 5 expensive items',
    category: 'wardrobe',
    difficulty: 'intermediate',
    xpReward: 200,
    levelRequired: 4,
    type: 'wardrobe_audit',
    requirements: [
      {
        id: 'audit-5-expensive',
        description: 'Calculate cost-per-wear for 5 expensive items',
        type: 'research',
        target: 5,
      },
    ],
    hints: [
      'Check purchase prices and divide by number of wears',
      'Items worn frequently have the lowest cost-per-wear',
      'Compare results to see which purchases were worth it',
    ],
    icon: 'Calculator',
    color: '#06b6d4',
    relatedModules: ['cost-per-wear-thinking'],
    order: 14,
  },

  {
    id: 'seasonal-swap',
    title: 'Seasonal Swap',
    description:
      'Rotate your wardrobe for the upcoming season. Store off-season items and surface seasonal pieces.',
    shortDescription: 'Rotate for the new season',
    category: 'wardrobe',
    difficulty: 'intermediate',
    xpReward: 225,
    levelRequired: 4,
    type: 'wardrobe_audit',
    requirements: [
      {
        id: 'scan-seasonal-items',
        description: 'Scan 5 newly surfaced seasonal items',
        type: 'scan_items',
        target: 5,
      },
    ],
    hints: [
      'Use breathable garment bags for off-season storage',
      'Organize seasonal items by category for easy access',
      'Check stored items for damage before putting them away',
    ],
    icon: 'Sun',
    color: '#06b6d4',
    relatedModules: ['seasonal-rotation-strategy'],
    order: 15,
  },

  {
    id: 'wardrobe-purge',
    title: 'The Edit',
    description:
      'Identify and remove 5 items that no longer serve your wardrobe. Quality over quantity.',
    shortDescription: 'Remove 5 items',
    category: 'wardrobe',
    difficulty: 'intermediate',
    xpReward: 200,
    levelRequired: 4,
    type: 'wardrobe_audit',
    requirements: [
      {
        id: 'remove-5-items',
        description: 'Identify 5 items to remove from your wardrobe',
        type: 'research',
        target: 5,
      },
    ],
    hints: [
      "If you haven't worn it in a year, it's time to let go",
      'Check for wear, damage, and poor fit',
      "Don't keep items just because of emotional attachment",
    ],
    icon: 'Scissors',
    color: '#06b6d4',
    relatedModules: ['wardrobe-editing'],
    order: 16,
  },

  // ==========================================
  // LEVEL 5: Identity -- "Define your style"
  // ==========================================
  {
    id: 'style-mood-board',
    title: 'Style Vision Board',
    description:
      'Curate 10 outfit inspirations that represent your personal style direction. Define your aesthetic.',
    shortDescription: 'Curate 10 inspirations',
    category: 'style',
    difficulty: 'advanced',
    xpReward: 250,
    levelRequired: 5,
    type: 'research_task',
    requirements: [
      {
        id: 'curate-10-inspirations',
        description: 'Curate 10 outfit inspirations',
        type: 'research',
        target: 10,
      },
    ],
    hints: [
      'Look at style icons whose aesthetic resonates with you',
      'Save looks from magazines, social media, and street style',
      "Find patterns in what you're drawn to",
    ],
    icon: 'Image',
    color: '#f59e0b',
    relatedModules: ['finding-personal-style', 'style-icons-inspiration'],
    order: 17,
  },

  {
    id: 'trend-filter',
    title: 'Trend Filter',
    description:
      'Identify 3 current trends and create outfits that adapt each to your personal style.',
    shortDescription: 'Adapt 3 trends to your style',
    category: 'style',
    difficulty: 'advanced',
    xpReward: 275,
    levelRequired: 5,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'adapt-3-trends',
        description: 'Create 3 outfits adapting current trends to your style',
        type: 'create_outfit',
        target: 3,
        metadata: { tag: 'trend-adapted' },
      },
    ],
    hints: [
      'Choose trends that feel wearable for your lifestyle',
      'Adapt proportions and colours to suit your body and palette',
      'Keep your personal style as the foundation',
    ],
    icon: 'TrendingUp',
    color: '#f59e0b',
    relatedModules: ['adapting-trends'],
    order: 18,
  },

  {
    id: 'signature-outfit',
    title: 'Signature Move',
    description:
      'Build and photograph your signature outfit - the look people think of when they picture you.',
    shortDescription: 'Create your signature look',
    category: 'style',
    difficulty: 'advanced',
    xpReward: 300,
    levelRequired: 5,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'create-signature',
        description: 'Create and save your signature outfit',
        type: 'create_outfit',
        target: 1,
        metadata: { tag: 'signature' },
      },
    ],
    hints: [
      'Think about what reflects your personality',
      'Comfort and confidence matter more than trends',
      'Include a distinctive element that sets you apart',
    ],
    icon: 'Fingerprint',
    color: '#f59e0b',
    relatedModules: ['building-signature-looks'],
    order: 19,
  },

  {
    id: 'style-evolution-log',
    title: 'Style Timeline',
    description:
      'Log outfits for 14 days documenting your style choices. Track your evolution and consistency.',
    shortDescription: '14-day style diary',
    category: 'style',
    difficulty: 'advanced',
    xpReward: 300,
    levelRequired: 5,
    timeLimit: 14,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'log-14-days',
        description: 'Log outfits for 14 consecutive days',
        type: 'log_outfit',
        target: 14,
      },
    ],
    hints: [
      'Be consistent with daily logging',
      "Note what feels right and what doesn't",
      'Review your entries weekly to spot patterns',
    ],
    icon: 'Clock',
    color: '#f59e0b',
    relatedModules: ['finding-personal-style', 'building-signature-looks'],
    order: 20,
  },

  // ==========================================
  // LEVEL 6: Mastery -- "Advanced styling techniques"
  // ==========================================
  {
    id: 'pattern-mix-outfits',
    title: 'Pattern Play',
    description:
      'Create 3 outfits that successfully combine 2+ patterns each. Master the art of pattern mixing.',
    shortDescription: '3 multi-pattern outfits',
    category: 'style',
    difficulty: 'advanced',
    xpReward: 300,
    levelRequired: 6,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'mix-patterns-3',
        description: 'Create 3 outfits combining 2+ patterns each',
        type: 'create_outfit',
        target: 3,
        metadata: { tag: 'pattern-mixed' },
      },
    ],
    hints: [
      'Vary the scale of patterns - pair large with small',
      'Share a common colour between patterns',
      'Keep one pattern subtle and one bold',
    ],
    icon: 'Grid',
    color: '#1e293b',
    relatedModules: ['pattern-mixing'],
    order: 21,
  },

  {
    id: 'texture-contrast',
    title: 'Texture Master',
    description:
      'Build 3 outfits that feature deliberate texture contrast. Combine smooth with rough, matte with sheen.',
    shortDescription: '3 texture-rich outfits',
    category: 'style',
    difficulty: 'advanced',
    xpReward: 275,
    levelRequired: 6,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'texture-contrast-3',
        description: 'Create 3 outfits with deliberate texture contrast',
        type: 'create_outfit',
        target: 3,
        metadata: { tag: 'texture-play' },
      },
    ],
    hints: [
      'Pair opposites: smooth leather with chunky knit',
      'Consider seasonal textures for visual interest',
      'Avoid outfits where every piece has the same fabric weight',
    ],
    icon: 'Layers',
    color: '#1e293b',
    relatedModules: ['texture-play'],
    order: 22,
  },

  {
    id: 'proportion-experiment',
    title: 'Silhouette Study',
    description:
      'Experiment with 3 different silhouettes: slim, relaxed, and structured. Find what flatters you most.',
    shortDescription: 'Try 3 silhouettes',
    category: 'style',
    difficulty: 'advanced',
    xpReward: 275,
    levelRequired: 6,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'silhouette-3',
        description: 'Create 3 outfits with different silhouettes',
        type: 'create_outfit',
        target: 3,
        metadata: { tag: 'proportion-study' },
      },
    ],
    hints: [
      'Slim fit creates a streamlined look',
      'Relaxed fits need structure elsewhere to avoid looking sloppy',
      'Photograph each silhouette for side-by-side comparison',
    ],
    icon: 'Maximize',
    color: '#1e293b',
    relatedModules: ['proportions-silhouette'],
    order: 23,
  },

  {
    id: 'advanced-color-outfit',
    title: 'Colour Virtuoso',
    description:
      'Create an outfit using 4+ colors in deliberate harmony. Apply advanced color theory to your wardrobe.',
    shortDescription: '4+ color outfit',
    category: 'color',
    difficulty: 'advanced',
    xpReward: 325,
    levelRequired: 6,
    type: 'outfit_creation',
    requirements: [
      {
        id: 'advanced-color-1',
        description: 'Create an outfit with 4+ colours in harmony',
        type: 'create_outfit',
        target: 1,
        metadata: { tag: 'advanced-color', minColors: 4 },
      },
    ],
    hints: [
      'Use one dominant colour and let others play supporting roles',
      'Analogous colour groups create easy harmony',
      'One accent pop against neutrals adds controlled boldness',
    ],
    icon: 'Palette',
    color: '#1e293b',
    relatedModules: ['advanced-color-coordination'],
    order: 24,
  },
]

/**
 * Get challenge by ID
 */
export function getChallengeById(id: string): Challenge | undefined {
  return CHALLENGES.find((challenge) => challenge.id === id)
}

/**
 * Get challenges by level
 */
export function getChallengesByLevel(level: number): Challenge[] {
  return CHALLENGES.filter((challenge) => challenge.levelRequired === level).sort(
    (a, b) => a.order - b.order
  )
}

/**
 * Get challenges by category
 */
export function getChallengesByCategory(category: string): Challenge[] {
  return CHALLENGES.filter((challenge) => challenge.category === category).sort(
    (a, b) => a.order - b.order
  )
}

/**
 * Check if challenge is unlocked
 */
export function isChallengeUnlocked(challengeId: string, userLevel: number): boolean {
  const challenge = getChallengeById(challengeId)
  return challenge ? userLevel >= challenge.levelRequired : false
}

/**
 * Get available challenges for user
 */
export function getAvailableChallenges(
  userLevel: number,
  completedChallengeIds: string[]
): Challenge[] {
  return CHALLENGES.filter(
    (challenge) =>
      challenge.levelRequired <= userLevel && !completedChallengeIds.includes(challenge.id)
  ).sort((a, b) => a.order - b.order)
}

/**
 * Calculate total XP from challenges
 */
export function getTotalChallengeXP(completedChallengeIds: string[]): number {
  return CHALLENGES.filter((c) => completedChallengeIds.includes(c.id)).reduce(
    (sum, c) => sum + c.xpReward,
    0
  )
}
